import { useState } from "react";
import { useAdminCampaigns, CampaignFormData } from "@/hooks/useAdminCampaigns";
import { Campaign } from "@/hooks/useActiveCampaign";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";

interface CampaignFormProps {
  campaign: Campaign | null;
  onClose: () => void;
}

const CampaignForm = ({ campaign, onClose }: CampaignFormProps) => {
  const { createCampaign, updateCampaign } = useAdminCampaigns();
  const isEditing = !!campaign;

  const [formData, setFormData] = useState<CampaignFormData>({
    name: campaign?.name || "",
    slug: campaign?.slug || "",
    description: campaign?.description || "",
    start_date: campaign?.start_date
      ? format(new Date(campaign.start_date), "yyyy-MM-dd'T'HH:mm")
      : "",
    end_date: campaign?.end_date
      ? format(new Date(campaign.end_date), "yyyy-MM-dd'T'HH:mm")
      : "",
    is_active: campaign?.is_active ?? true,
    is_hidden: campaign?.is_hidden ?? false,
    hero_title: campaign?.hero_title || "",
    hero_subtitle: campaign?.hero_subtitle || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: new Date(formData.end_date).toISOString(),
    };

    if (isEditing) {
      await updateCampaign.mutateAsync({ id: campaign.id, data: submitData });
    } else {
      await createCampaign.mutateAsync(submitData);
    }
    onClose();
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold">
          {isEditing ? "Edit Campaign" : "New Campaign"}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Campaign Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  name: e.target.value,
                  slug: !isEditing ? generateSlug(e.target.value) : formData.slug,
                });
              }}
              placeholder="New Year New You"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">URL Slug *</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) =>
                setFormData({ ...formData, slug: generateSlug(e.target.value) })
              }
              placeholder="new-year-new-you"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Campaign description..."
            rows={2}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="hero_title">Hero Title</Label>
            <Input
              id="hero_title"
              value={formData.hero_title}
              onChange={(e) =>
                setFormData({ ...formData, hero_title: e.target.value })
              }
              placeholder="✨ New Year, New You ✨"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
            <Input
              id="hero_subtitle"
              value={formData.hero_subtitle}
              onChange={(e) =>
                setFormData({ ...formData, hero_subtitle: e.target.value })
              }
              placeholder="Exclusive offers to kickstart 2025"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start_date">Start Date *</Label>
            <Input
              id="start_date"
              type="datetime-local"
              value={formData.start_date}
              onChange={(e) =>
                setFormData({ ...formData, start_date: e.target.value })
              }
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="end_date">End Date *</Label>
            <Input
              id="end_date"
              type="datetime-local"
              value={formData.end_date}
              onChange={(e) =>
                setFormData({ ...formData, end_date: e.target.value })
              }
              required
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="is_active"
            checked={formData.is_active}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, is_active: checked })
            }
          />
          <Label htmlFor="is_active">Campaign Active</Label>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Switch
              id="is_hidden"
              checked={formData.is_hidden}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_hidden: checked })
              }
            />
            <Label htmlFor="is_hidden">Hidden (Partner Campaign)</Label>
          </div>
          <p className="text-xs text-muted-foreground ml-10">
            Hidden campaigns are only accessible via their unique URL slug and won't appear in the main Offers page or navigation.
          </p>
          {formData.is_hidden && formData.slug && (
            <div className="ml-10 p-2 bg-muted rounded text-sm font-mono">
              app.nimi.live/offers/{formData.slug}
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createCampaign.isPending || updateCampaign.isPending}
          >
            {isEditing ? "Update Campaign" : "Create Campaign"}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default CampaignForm;
